import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import { useOrderingV2Ui } from 'src/contexts/ordering-v2-ui-context';
import { STORY_GROUPS } from './stories-config';

const SLIDE_MS = 6000;

export default function OrderV2StoryModal() {
  const { storyState, closeStory, setStorySlide } = useOrderingV2Ui();
  const [progress, setProgress] = useState(0);

  const group = useMemo(
    () => STORY_GROUPS.find((g) => g.id === storyState?.groupId),
    [storyState?.groupId]
  );
  const slides = group?.slides || [];
  const idx = storyState?.slideIndex ?? 0;
  const slide = slides[idx];
  const n = slides.length;

  useEffect(() => {
    if (!storyState || !n) return undefined;
    setProgress(0);
    const t0 = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - t0;
      setProgress(Math.min(100, (elapsed / SLIDE_MS) * 100));
      if (elapsed >= SLIDE_MS) {
        clearInterval(id);
        if (idx < n - 1) {
          setStorySlide(idx + 1);
        } else {
          closeStory();
        }
      }
    }, 50);
    return () => clearInterval(id);
  }, [storyState, idx, n, closeStory, setStorySlide]);

  const goPrev = useCallback(() => {
    if (idx > 0) setStorySlide(idx - 1);
  }, [idx, setStorySlide]);

  const goNext = useCallback(() => {
    if (idx < n - 1) setStorySlide(idx + 1);
    else closeStory();
  }, [idx, n, closeStory, setStorySlide]);

  if (!storyState || !group || !slide) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (t) => t.zIndex.modal + 2,
        bgcolor: alpha('#000', 0.88),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          minHeight: '70vh',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: '#1a1a1a',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack direction="row" spacing={0.5} sx={{ px: 1.5, pt: 1.5 }}>
          {slides.map((_, i) => {
            let barValue = 0;
            if (i < idx) barValue = 100;
            else if (i === idx) barValue = progress;
            return (
            <LinearProgress
              key={i}
              variant="determinate"
              value={barValue}
              sx={{
                flex: 1,
                height: 3,
                borderRadius: 1,
                bgcolor: alpha('#fff', 0.2),
                '& .MuiLinearProgress-bar': { bgcolor: '#fff' },
              }}
            />
            );
          })}
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: group.color,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              {group.title[0]}
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{group.title}</Typography>
              <Typography sx={{ color: alpha('#fff', 0.55), fontSize: 12 }}>
                {idx + 1} of {n}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={closeStory} sx={{ color: '#fff' }} aria-label="Close story">
            <Iconify icon="eva:close-fill" width={24} />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
          {slide.badge && (
            <Typography sx={{ color: group.color, fontWeight: 700, fontSize: 11, mb: 1, letterSpacing: 1 }}>
              {slide.badge}
            </Typography>
          )}
          <Box
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 180,
              bgcolor: alpha('#fff', 0.08),
              borderRadius: 2,
              mb: 2,
            }}
          />
          <Box
            sx={{
              width: '100%',
              bgcolor: '#fff',
              borderRadius: 3,
              p: 2,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {slide.title}
            </Typography>
            {slide.subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {slide.subtitle}
              </Typography>
            )}
            {slide.bullets?.length > 0 && (
              <>
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                  WHAT&apos;S INSIDE?
                </Typography>
                <Stack direction="row" spacing={2}>
                  {slide.bullets.map((b, i) => (
                    <Stack key={i} spacing={0.5} sx={{ flex: 1 }}>
                      {b.col?.map((line) => (
                        <Typography key={line} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: group.color }} />
                          {line}
                        </Typography>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 2 }}>
              <Box>
                {slide.priceWas ? (
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                    AED {slide.priceWas}
                  </Typography>
                ) : null}
                {slide.priceNow > 0 && (
                  <Typography variant="h6" sx={{ color: group.color, fontWeight: 800 }}>
                    AED {slide.priceNow}
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                sx={{ bgcolor: group.color, fontWeight: 700, borderRadius: 999, px: 3 }}
                onClick={closeStory}
              >
                Order Now
              </Button>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 80,
            bottom: 120,
            left: 0,
            width: '28%',
            cursor: 'w-resize',
          }}
          onClick={goPrev}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 80,
            bottom: 120,
            right: 0,
            width: '28%',
            cursor: 'e-resize',
          }}
          onClick={goNext}
        />
      </Box>
    </Box>
  );
}
